import { AnyAction } from 'redux';

import { App, Leistung, Produkt, ZusatzoptionItem } from '@ps20-frontend/frontend-models';
import { produktReducer } from '@ps20-frontend/reducers';
import cloneDeep from 'lodash/cloneDeep';
import {
  GlobalAction,
  GlobalActions,
  GlobalProduktReceivedAction,
  GlobalUnfallVpUebernehmenAction,
  PreiseForDashboard,
} from '@ps20-frontend/shop-integration-redux';
import { Produktlinienname } from '@ps20/bff-models';
import {
  LeistungEigenschaftChangedAction,
  SelectedInvsummeChanged,
  SpecialProfiSportSelectedAction,
  UnfActions,
  ZusatzoptionInputChangedAction
} from '../unfActions';
import { IAppState } from '../appState';
import { UnfFachschluessel } from '../model/unf.enums';
import { isDateInRange } from '../selector/produkt.selector';
import { Person } from '../selector/personen.selector';

export const unfProduktReducer = function (
  sectionState: IAppState['produkt'] = null,
  action: AnyAction,
  state: IAppState,
): IAppState['produkt'] {
  const newSectionState = produktReducer(sectionState, action, App.UNF, state?.angebotsId);

  switch (action.type) {
    case GlobalActions.DASHBOARD_UNFALL_VP_UEBERNEHMEN:
    case GlobalActions.PREISE_RESET_DASHBOARD:
    case GlobalActions.PRODUKT_RECEIVED:
      return configureZusatzoption(newSectionState, action, state);
    case UnfActions.ZUSATZOPTIONINPUT_CHANGED:
      return changeZusatzoptionInput(newSectionState, action as ZusatzoptionInputChangedAction);
    case UnfActions.LEISTUNG_EIGENSCHAFT_CHANGED:
      return changeLeistungBetrag(newSectionState, action as LeistungEigenschaftChangedAction);
    case UnfActions.SELECTED_INVSUMME_CHANGED:
      return changeSelectedInvsumme(newSectionState, action as SelectedInvsummeChanged);
    case UnfActions.SPECIAL_PROFI_SPORT_ZUSATZOPTION:
      return changeProfiSportZusatzoption(newSectionState, action as SpecialProfiSportSelectedAction);
    default:
      return newSectionState;
  }
};

function configureZusatzoption(
  sectionState: Produkt,
  action:
    | GlobalUnfallVpUebernehmenAction
    | GlobalAction<PreiseForDashboard>
    | GlobalAction<GlobalProduktReceivedAction>,
  state: IAppState,
): Produkt {
  const angebotsId = action.payload.angebotsId;
  let vp;

  if (action.type === GlobalActions.DASHBOARD_UNFALL_VP_UEBERNEHMEN) {
    vp = (action as GlobalUnfallVpUebernehmenAction).payload.vp;
  }

  const geburtsdatum = vp ? vp?.geburtsdatum : state.vp?.geburtsdatum;
  if (state.angebotsId === angebotsId && geburtsdatum !== '') {
    const newProduktLinien = sectionState.produktlinien.map(linie => ({
      ...linie,
      zusatzoptionen: linie.zusatzoptionen?.map(option => {
        const beginnDatum = state.vertragsoptionen.beginnDatum;
        const verfuegbar = isZusatzoptionVerfuegbar(beginnDatum, option, geburtsdatum);
        return {
          ...option,
          visible: verfuegbar,
          disabled: !verfuegbar,
          isSelected: verfuegbar && (option.isSelected || Boolean(option.initialSelektiert)),
        };
      }),
    }));

    return {
      ...sectionState,
      produktlinien: newProduktLinien,
    };
  }
  return sectionState;
}

function isZusatzoptionVerfuegbar(
  beginnDatum: string,
  option: ZusatzoptionItem,
  geburtsdatum: Person['geburtsdatum'],
): boolean {
  const { fachschluessel } = option;
  const spezielleFS = [UnfFachschluessel.KINDERINVALIDITAET, UnfFachschluessel.RUNDUMSERVICE];
  const isStandardFS = !spezielleFS.includes(fachschluessel as UnfFachschluessel);

  const kisUndKindern =
    fachschluessel === UnfFachschluessel.KINDERINVALIDITAET &&
    isDateInRange(geburtsdatum, beginnDatum, 1, 17);
  const rundumUndErwachsenen =
    fachschluessel === UnfFachschluessel.RUNDUMSERVICE &&
    isDateInRange(geburtsdatum, beginnDatum, 17);

  return isStandardFS || kisUndKindern || rundumUndErwachsenen;
}

function changeLeistungBetrag(
  sectionState: Produkt,
  action: LeistungEigenschaftChangedAction,
): Produkt {
  const { id, value, produktName } = action.payload;

  const newState: Produkt = cloneDeep(sectionState);
  const produkt = newState.produktlinien.find(linie => linie.produktName === produktName);
  const leistung = produkt.leistungen.find(leistung => leistung.id === id);
  leistung.eigenschaft = value;

  return leistung.fachschluessel === UnfFachschluessel.INV_SUMME
    ? changeInvsumme(newState, value)
    : newState;
}

function changeZusatzoptionInput(
  sectionState: Produkt,
  action: ZusatzoptionInputChangedAction,
): Produkt {
  const newState = cloneDeep(sectionState);
  newState.produktlinien.forEach(
    pl =>
      (pl.zusatzoptionen.find(item => item.id === action.payload.id).eigenschaft =
        action.payload.value),
  );

  return newState;
}

function changeProfiSportZusatzoption(sectionState: Produkt,
                                      action: SpecialProfiSportSelectedAction): Produkt {
  const newState: Produkt = cloneDeep(sectionState);
  for (const pl of newState.produktlinien) {
    const akutleistung = pl.zusatzoptionen.find(item => item.name === 'AKUTLEISTUNG');
    if (!akutleistung) {
      continue;
    }
    if (action.payload.selected) {
      akutleistung.isSelected = false;
    }
    if (pl.produktName === Produktlinienname.PREMIUM) {
      akutleistung.isSelected = !action.payload.selected;
      akutleistung.initialSelektiert = !action.payload.selected;
    }
  }
  return newState;
}

function changeSelectedInvsumme(
  sectionState: IAppState['produkt'],
  action: SelectedInvsummeChanged,
): IAppState['produkt'] {
  const newState: IAppState['produkt'] = cloneDeep(sectionState);
  return changeInvsumme(newState, action.payload.invSumme);
}

function changeInvsumme(
  sectionState: IAppState['produkt'],
  newInvSumme: Leistung['eigenschaft'],
): IAppState['produkt'] {
  sectionState.produktlinien.forEach(produktLinie => {
    const invSummeLeistung = produktLinie.leistungen.find(
      leistung => leistung.fachschluessel === UnfFachschluessel.INV_SUMME,
    );
    invSummeLeistung.eigenschaft = newInvSumme;
  });
  return changeMaxInvSumme(sectionState, newInvSumme);
}

function changeMaxInvSumme(
  sectionState: IAppState['produkt'],
  invSumme: Leistung['eigenschaft'],
): IAppState['produkt'] {
  const maxInvSumme = Number(invSumme) * 5;
  sectionState.produktlinien.forEach(produktlinie => {
    produktlinie.leistungen.find(
      le => le.fachschluessel === UnfFachschluessel.MAX_LEISTUNG,
    ).eigenschaft = maxInvSumme.toString();
  });

  return sectionState;
}
